import { nocodeBackendService } from './nocodeBackendService';
import { dropboxService } from './dropboxService';
import { githubService } from './githubService';

class CRMService {
  async getContacts() {
    try {
      const result = await nocodeBackendService.read('contacts');

      const rows = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];

      return rows;
    } catch (error) {
      console.error('[CRM] Failed to fetch contacts', error);
      return [];
    }
  }

  async createContact(contactData, profilePicture = null) {
    console.log('🚀 [CRM]=====CREATE CONTACT START=====');
    console.log('📋 [CRM] Contact data:', contactData);
    console.log('🖼️ [CRM] Profile picture:', profilePicture ? profilePicture.name : 'None');
    
    try {
      let profilePictureData = null;

      // First, create the contact without the profile picture
      console.log('📝 [CRM] Step 1: Creating base contact record...');
      const baseContactData = {
        ...contactData,
        profile_picture_url: '',
        profile_picture_thumbnail: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      console.log('📤 [CRM] Sending to NocodeBackend:', baseContactData);
      
      const contactResponse = await nocodeBackendService.create('contacts', baseContactData);
      console.log('📥 [CRM] NocodeBackend response:', contactResponse);

      const d = contactResponse?.data;

      const contactId =
        d?.id ??
        (Array.isArray(d) ? d[0]?.id : undefined) ??
        contactResponse?.id ??
        d?.record?.id ??
        d?.insertedId;

      if (!contactId) {
        console.error('[CRM] No contact ID returned from database', { contactResponse });
        throw new Error('No contact ID returned from database');
      }

      console.log('🆔 [CRM] Extracted contact ID:', contactId);

      // Upload profile picture if provided
      if (profilePicture && contactId) {
        console.log('📸 [CRM] Step 2: Uploading profile picture...');
        console.log('📁 [CRM] File details:', {
          name: profilePicture.name,
          size: profilePicture.size,
          type: profilePicture.type
        });
        
        try {
          profilePictureData = await dropboxService.uploadProfilePicture(profilePicture, contactId);
          console.log('✅ [CRM] Profile picture uploaded successfully:', profilePictureData);
          
          // Update the contact with the profile picture URLs
          console.log('🔄 [CRM] Step 3: Updating contact with profile picture URLs...');
          const updateData = {
            profile_picture_url: profilePictureData.url,
            profile_picture_thumbnail: profilePictureData.thumbnailUrl,
            updated_at: new Date().toISOString()
          };
          console.log('📤 [CRM] Sending update to NocodeBackend:', updateData);
          
          const updateResponse = await nocodeBackendService.update('contacts', contactId, updateData);
          console.log('📥 [CRM] Update response:', updateResponse);
        } catch (dropboxError) {
          console.error('❌ [CRM] Dropbox upload failed:', dropboxError);
          console.log('⚠️ [CRM] Continuing without profile picture...');
        }
      } else {
        console.log('ℹ️ [CRM] No profile picture to upload');
      }

      // Create GitHub audit log
      console.log('📝 [CRM] Step 4: Creating GitHub audit log...');
      try {
        await githubService.createCommit('contacts', `Added new contact: ${contactData.name}`);
        console.log('✅ [CRM] GitHub audit log created');
      } catch (githubError) {
        console.error('⚠️ [CRM] GitHub audit log failed (non-critical):', githubError);
      }

      // Return the complete contact data
      const finalContactData = {
        ...contactData,
        id: contactId,
        profile_picture_url: profilePictureData?.url || '',
        profile_picture_thumbnail: profilePictureData?.thumbnailUrl || ''
      };
      console.log('✅ [CRM]=====CREATE CONTACT SUCCESS=====');
      console.log('📋 [CRM] Final contact data:', finalContactData);
      
      return finalContactData;
    } catch (error) {
      console.error('❌ [CRM]=====CREATE CONTACT FAILED=====');
      console.error('💥 [CRM] Error details:', error);
      console.error('📋 [CRM] Original contact data:', contactData);
      console.error('🖼️ [CRM] Profile picture info:', profilePicture ? {
        name: profilePicture.name,
        size: profilePicture.size,
        type: profilePicture.type
      } : 'None');
      console.error('🔗 [CRM] Error stack:', error.stack);
      throw new Error('Failed to create contact: ' + error.message);
    }
  }

  async updateContact(id, contactData, profilePicture = null) {
    console.log('🔄 [CRM]=====UPDATE CONTACT START=====');
    console.log('🆔 [CRM] Contact ID:', id);
    console.log('📋 [CRM] Contact data:', contactData);
    console.log('🖼️ [CRM] Profile picture:', profilePicture ? profilePicture.name : 'None');
    
    try {
      let updateData = { ...contactData };

      // Handle profile picture update
      if (profilePicture) {
        console.log('📸 [CRM] Uploading new profile picture...');
        const profilePictureData = await dropboxService.uploadProfilePicture(profilePicture, id);
        console.log('✅ [CRM] Profile picture uploaded:', profilePictureData);
        updateData.profile_picture_url = profilePictureData.url;
        updateData.profile_picture_thumbnail = profilePictureData.thumbnailUrl;
      }

      updateData.updated_at = new Date().toISOString();
      console.log('📤 [CRM] Sending update to NocodeBackend:', updateData);
      
      const response = await nocodeBackendService.update('contacts', id, updateData);
      console.log('📥 [CRM] Update response:', response);

      // Create GitHub audit log
      try {
        await githubService.createCommit('contacts', `Updated contact: ${contactData.name}`);
        console.log('✅ [CRM] GitHub audit log created');
      } catch (githubError) {
        console.error('⚠️ [CRM] GitHub audit log failed (non-critical):', githubError);
      }

      console.log('✅ [CRM]=====UPDATE CONTACT SUCCESS=====');
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM]=====UPDATE CONTACT FAILED=====');
      console.error('💥 [CRM] Error details:', error);
      console.error('🆔 [CRM] Contact ID:', id);
      console.error('📋 [CRM] Contact data:', contactData);
      console.error('🔗 [CRM] Error stack:', error.stack);
      throw new Error('Failed to update contact: ' + error.message);
    }
  }

  async deleteContact(id) {
    console.log('🗑️ [CRM]=====DELETE CONTACT START=====');
    console.log('🆔 [CRM] Contact ID:', id);
    
    try {
      // Get contact info to delete profile picture
      console.log('📋 [CRM] Fetching contact info for cleanup...');
      const contactResponse = await nocodeBackendService.readById('contacts', id);
      console.log('📥 [CRM] Contact info:', contactResponse);

      if (contactResponse.data?.profile_picture_url) {
        console.log('🗑️ [CRM] Deleting profile picture from Dropbox...');
        // Extract path from URL and delete from Dropbox
        const urlParts = contactResponse.data.profile_picture_url.split('/');
        const fileName = urlParts[urlParts.length - 1].replace('?dl=0', '');
        console.log('📁 [CRM] File to delete:', fileName);
        
        try {
          await dropboxService.deleteFile(`/crm/profile-pictures/${fileName}`);
          console.log('✅ [CRM] Profile picture deleted from Dropbox');
        } catch (dropboxError) {
          console.error('⚠️ [CRM] Dropbox deletion failed (non-critical):', dropboxError);
        }
      }

      console.log('🗑️ [CRM] Deleting contact from database...');
      await nocodeBackendService.delete('contacts', id);
      console.log('✅ [CRM] Contact deleted from database');

      // Create GitHub audit log
      try {
        await githubService.createCommit('contacts', `Deleted contact with ID: ${id}`);
        console.log('✅ [CRM] GitHub audit log created');
      } catch (githubError) {
        console.error('⚠️ [CRM] GitHub audit log failed (non-critical):', githubError);
      }

      console.log('✅ [CRM]=====DELETE CONTACT SUCCESS=====');
      return true;
    } catch (error) {
      console.error('❌ [CRM]=====DELETE CONTACT FAILED=====');
      console.error('💥 [CRM] Error details:', error);
      console.error('🆔 [CRM] Contact ID:', id);
      console.error('🔗 [CRM] Error stack:', error.stack);
      throw new Error('Failed to delete contact: ' + error.message);
    }
  }

  async getLeads() {
    try {
      console.log('🔍 [CRM] Fetching leads...');
      const response = await nocodeBackendService.read('leads');
      console.log('✅ [CRM] Leads fetched successfully');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error fetching leads:', error);
      return [];
    }
  }

  async createLead(leadData) {
    console.log('🚀 [CRM] Creating lead:', leadData);
    try {
      const response = await nocodeBackendService.create('leads', {
        ...leadData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Lead created successfully');
      await githubService.createCommit('leads', `Added new lead: ${leadData.company}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Create lead error:', error);
      throw new Error('Failed to create lead: ' + error.message);
    }
  }

  async updateLead(id, leadData) {
    console.log('🔄 [CRM] Updating lead:', id, leadData);
    try {
      const response = await nocodeBackendService.update('leads', id, {
        ...leadData,
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Lead updated successfully');
      await githubService.createCommit('leads', `Updated lead: ${leadData.company}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Update lead error:', error);
      throw new Error('Failed to update lead: ' + error.message);
    }
  }

  async getDeals() {
    try {
      console.log('🔍 [CRM] Fetching deals...');
      const response = await nocodeBackendService.read('deals');
      console.log('✅ [CRM] Deals fetched successfully');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error fetching deals:', error);
      return [];
    }
  }

  async createDeal(dealData) {
    console.log('🚀 [CRM] Creating deal:', dealData);
    try {
      const response = await nocodeBackendService.create('deals', {
        ...dealData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Deal created successfully');
      await githubService.createCommit('deals', `Added new deal: ${dealData.title}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Create deal error:', error);
      throw new Error('Failed to create deal: ' + error.message);
    }
  }

  async updateDeal(id, dealData) {
    console.log('🔄 [CRM] Updating deal:', id, dealData);
    try {
      const response = await nocodeBackendService.update('deals', id, {
        ...dealData,
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Deal updated successfully');
      await githubService.createCommit('deals', `Updated deal: ${dealData.title}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Update deal error:', error);
      throw new Error('Failed to update deal: ' + error.message);
    }
  }

  async getTasks() {
    try {
      console.log('🔍 [CRM] Fetching tasks...');
      const response = await nocodeBackendService.read('tasks');
      console.log('✅ [CRM] Tasks fetched successfully');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error fetching tasks:', error);
      return [];
    }
  }

  async createTask(taskData) {
    console.log('🚀 [CRM] Creating task:', taskData);
    try {
      const response = await nocodeBackendService.create('tasks', {
        ...taskData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Task created successfully');
      await githubService.createCommit('tasks', `Added new task: ${taskData.title}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Create task error:', error);
      throw new Error('Failed to create task: ' + error.message);
    }
  }

  async updateTask(id, taskData) {
    console.log('🔄 [CRM] Updating task:', id, taskData);
    try {
      const response = await nocodeBackendService.update('tasks', id, {
        ...taskData,
        updated_at: new Date().toISOString()
      });
      console.log('✅ [CRM] Task updated successfully');
      await githubService.createCommit('tasks', `Updated task: ${taskData.title}`);
      return response.data || response;
    } catch (error) {
      console.error('❌ [CRM] Update task error:', error);
      throw new Error('Failed to update task: ' + error.message);
    }
  }

  async uploadFile(file, type = 'general') {
    console.log('📁 [CRM] Uploading file:', file.name, 'to:', type);
    try {
      const response = await dropboxService.uploadFile(file, `crm/${type}/${Date.now()}_${file.name}`);
      console.log('✅ [CRM] File uploaded successfully');
      return response;
    } catch (error) {
      console.error('❌ [CRM] Upload file error:', error);
      throw new Error('Failed to upload file: ' + error.message);
    }
  }

  // Search functionality
  async searchContacts(searchData) {
    console.log('🔍 [CRM] Searching contacts:', searchData);
    try {
      const response = await nocodeBackendService.search('contacts', searchData);
      console.log('✅ [CRM] Contacts search completed');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error searching contacts:', error);
      return [];
    }
  }

  async searchLeads(searchData) {
    console.log('🔍 [CRM] Searching leads:', searchData);
    try {
      const response = await nocodeBackendService.search('leads', searchData);
      console.log('✅ [CRM] Leads search completed');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error searching leads:', error);
      return [];
    }
  }

  async searchDeals(searchData) {
    console.log('🔍 [CRM] Searching deals:', searchData);
    try {
      const response = await nocodeBackendService.search('deals', searchData);
      console.log('✅ [CRM] Deals search completed');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error searching deals:', error);
      return [];
    }
  }

  async searchTasks(searchData) {
    console.log('🔍 [CRM] Searching tasks:', searchData);
    try {
      const response = await nocodeBackendService.search('tasks', searchData);
      console.log('✅ [CRM] Tasks search completed');
      return response.data || [];
    } catch (error) {
      console.error('❌ [CRM] Error searching tasks:', error);
      return [];
    }
  }
}

export const crmService = new CRMService();